import React from 'react';
import { Link } from 'react-router-dom';
import "../../styles/Css/FeaturesSingle.css";

const Features = () => {
    const mainFeatures = [
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
            ),
            title: 'AI Contract Intelligence',
            description: 'Our AI automatically reads and analyzes your real estate contracts, extracting key terms, dates, contingencies, and important clauses. No more manual scanning through pages of documents.',
            benefits: [
                'Automatic extraction of key dates and deadlines',
                'Contingency tracking and alerts',
                'Clause identification and summarization',
                'Risk assessment and flagging'
            ]
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                </svg>
            ),
            title: 'Smart Email Management',
            description: 'Get instant summaries of long email threads and AI-generated response suggestions. Keep your communication flowing smoothly without spending hours in your inbox.',
            benefits: [
                'Thread summarization in seconds',
                'Smart reply suggestions',
                'Priority inbox sorting',
                'Automated follow-up reminders'
            ]
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2z" />
                    <path d="M19 3h-4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" />
                </svg>
            ),
            title: 'Automated Task Management',
            description: 'Let AI create and manage your task lists with automatic reminders and deadline tracking. Never miss a critical step in your transaction process.',
            benefits: [
                'Auto-generated task checklists',
                'Smart deadline tracking',
                'Progress monitoring',
                'Team task assignment'
            ]
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="9" y1="21" x2="9" y2="9" />
                </svg>
            ),
            title: 'CRM & Platform Integrations',
            description: 'Seamlessly connect with your existing CRM, email, calendar, and other tools. Create a unified workflow without switching between multiple applications.',
            benefits: [
                'Connect with major CRMs',
                'Calendar synchronization',
                'Email platform integration',
                'MLS data connectivity'
            ]
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <line x1="9" y1="9" x2="9.01" y2="9" />
                    <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
            ),
            title: 'Client Portal',
            description: 'Give your clients a dedicated space to review documents, track progress, and communicate. Impress clients with a professional, branded experience.',
            benefits: [
                'Branded client experience',
                'Document sharing and signing',
                'Real-time progress updates',
                'Secure messaging'
            ]
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
            ),
            title: 'Analytics & Reporting',
            description: 'Track your transaction performance with insightful dashboards. Understand your business better with data-driven insights and comprehensive reports.',
            benefits: [
                'Performance dashboards',
                'Transaction analytics',
                'Time tracking reports',
                'Revenue forecasting'
            ]
        }
    ];

    const integrations = [
        { name: 'Salesforce', category: 'CRM' },
        { name: 'Follow Up Boss', category: 'CRM' },
        { name: 'kvCORE', category: 'CRM' },
        { name: 'Gmail', category: 'Email' },
        { name: 'Outlook', category: 'Email' },
        { name: 'Google Calendar', category: 'Calendar' },
        { name: 'DocuSign', category: 'Documents' },
        { name: 'Dotloop', category: 'Documents' },
        { name: 'Zillow', category: 'MLS' },
        { name: 'Realtor.com', category: 'MLS' },
        { name: 'Slack', category: 'Communication' },
        { name: 'Zapier', category: 'Automation' }
    ];

    return (
        <div className="features-page">
            {/* Hero */}
            <section className="features-hero">
                <div className="container">
                    <span className="badge">Features</span>
                    <h1 className="features-hero__title">
                        AI-Powered Features Built for<br />
                        <span className="gradient-text">Real Estate Professionals</span>
                    </h1>
                    <p className="features-hero__subtitle">
                        Intelligent automation that works for you, 24/7. Discover how Deal to Close AI
                        can transform your transaction management.
                    </p>
                </div>
            </section>

            {/* Main Features */}
            <section className="features-main section">
                <div className="container">
                    {mainFeatures.map((feature, index) => (
                        <div
                            key={index}
                            className={`feature-detail ${index % 2 === 1 ? 'feature-detail--reverse' : ''}`}
                        >
                            <div className="feature-detail__content">
                                <div className="feature-detail__icon">
                                    {feature.icon}
                                </div>
                                <h2 className="feature-detail__title">{feature.title}</h2>
                                <p className="feature-detail__description">{feature.description}</p>
                                <ul className="feature-detail__benefits">
                                    {feature.benefits.map((benefit, i) => (
                                        <li key={i}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="feature-detail__visual">
                                <div className="feature-detail__preview">
                                    <div className="feature-detail__preview-content">
                                        <div className="feature-detail__preview-header">
                                            <span></span><span></span><span></span>
                                        </div>
                                        <div className="feature-detail__preview-body">
                                            <div className="feature-detail__preview-block"></div>
                                            <div className="feature-detail__preview-block feature-detail__preview-block--small"></div>
                                            <div className="feature-detail__preview-block feature-detail__preview-block--medium"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Integrations */}
            <section className="integrations section" id="integrations">
                <div className="container">
                    <h2 className="section-title">Seamless Integrations</h2>
                    <p className="section-subtitle">
                        Connect with the tools you already use and love.
                    </p>
                    <div className="integrations__grid">
                        {integrations.map((integration, index) => (
                            <div key={index} className="integration-card">
                                <div className="integration-card__icon">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <path d="M12 8v8" />
                                        <path d="M8 12h8" />
                                    </svg>
                                </div>
                                <span className="integration-card__name">{integration.name}</span>
                                <span className="integration-card__category">{integration.category}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="features-cta">
                <div className="container">
                    <h2 className="features-cta__title">Ready to Transform Your Workflow?</h2>
                    <p className="features-cta__subtitle">
                        Join hundreds of real estate professionals already using Deal to Close AI.
                    </p>
                    <Link to="/#signup" className="btn btn-primary btn-lg">
                        Get Started Free
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                        </svg>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Features;
